import app from "@/main";
import {SnapshotEntity} from "./types";

/**
 * Faz 4 — sunucu snapshot deposunun İSTEMCİ OKUMA ucu (İP1).
 *
 * `GET /api/v1/sorgu-snapshot?dosya_ref=&alan=&kisi_kurum_id=[&vergi_no=]`
 *
 * TAZELİK KARARI BURADA VERİLMEZ: bu servis kaydı getirir, "taze mi" sorusunu
 * çağıran (`UyapRequest`) `tazelik.ts` eşiğiyle yanıtlar. Ayrım bilinçli —
 * eşik F5'te alan bazlı incelirse burası değişmez.
 *
 * `borclu_ref` çözümlemesi SUNUCUDA kalır (Faz 1 kararı D2): referans yerel
 * kisiler/kurumlar tablolarına bakılarak `vkn:`/`tckn:`/`kk:` olarak çözülüyor,
 * istemcinin elinde yalnız UYAP `kisiKurumId`'si var. Bu yüzden istek
 * `kisi_kurum_id` ile atılır, sunucu aday referans kümesini kendi kurar.
 *
 * HATA ZİNCİRİ KIRMAZ: sunucuya ulaşılamazsa/şekil beklenmedikse `null` döner ve
 * zincir bugünkü davranışa (IndexedDB -> canlı) sessizce düşer.
 */

// kod inceleme: bu arayüz SUNUCU SÖZLEŞMESİdir (API cevabının tam şekli) —
// istemci tarafı (UyapRequest.snapshotCevabiKur, bu dosyadaki damga() ve
// kayitlariAlanaGoreMemoyaYaz()) yalnızca alan/entities/govde/fetched_at/
// last_seen_at/taze_kadar alanlarını okur. borclu_ref/hash/hash_surum/kaynak/
// ozet bugün istemcide okunmuyor; alanlar API sözleşmesi için tutuluyor,
// kaldırılmamalı.
export interface SnapshotKaydi {
    alan: string;
    borclu_ref: string;
    hash: string;
    hash_surum: number;
    entities: SnapshotEntity[];
    ozet?: Record<string, unknown> | null;
    // K4: parse edilmiş HAM cevap (IndexedDB eşleniği). Doluysa okuma zinciri
    // cevap kurucuya gerek kalmadan DOĞRUDAN bundan beslenir (B1) — tüm gövdeli
    // alanlar (egm/sgkssk/takbis/gib dahil) zincire girer.
    govde?: unknown | null;
    kaynak: string;
    fetched_at: string | null;
    last_seen_at: string | null;
    // F5 (9 Eyl 2026 kullanıcı kararı): sunucunun bu (dosya_ref, alan) için
    // hesapladığı taze kalma sınırı (ISO-8601, +03:00) — plan frekansı 1 günden
    // büyükse sonGorulme + frekans_gun × 24 saat; hiçbir AKTİF planda yoksa
    // `null` (istemci `tazelik.tazeMi()` ile sabit 24 saat varsayar).
    taze_kadar?: string | null;
}

type Memo = {zaman: number, deger: Promise<SnapshotKaydi | null>, omurMs?: number};

/** `dosyayiOnYukle()`'nin borçlu listesi girdisi. */
export interface DosyaOnYuklemeBorclusu {
    kisiKurumId: number;
    vergiNo: string | null;
}

/**
 * Aynı sorgu bir koşumda İKİ KEZ sorulur: önce `DosyaBorclu.query*` kota kapısı
 * için `isDataCached()`, hemen ardından `run()`. İkisi de AYRI istek nesnesi
 * kurduğu için memo örnek üzerinde değil modül düzeyinde tutulur.
 *
 * ÖMRÜN ASIL İŞİ BİR TUTARLILIK GARANTİSİ: `isDataCached()` "taze snapshot var"
 * dediğinde `DosyaBorclu.query*` kota kapısını ATLIYOR. `run()` aynı cevabı
 * bulamazsa (memo düşmüş, sunucu bu kez hata vermiş) ÜCRETLİ canlı sorgu kota
 * kapısı olmadan koşardı — kullanıcının açmadığı bakiye harcanabilirdi. Memo bu
 * ikiliyi tek karara bağlar.
 *
 * 120 sn: iki çağrı pratikte arka arkaya geliyor, ama aradaki UYAP isteği
 * (yavaş ağ, oturum kurtarma turu) uzayabilir. Daha uzun tutulmadı — tazelik
 * "aynı gün" olsa da uzun bir memo, aynı oturumda az önce yazılmış bir
 * snapshot'ı gözden kaçırırdı.
 */
const MEMO_OMRU_MS = 120 * 1000;

/**
 * B32: `onYukle()` ile TOPLU doldurulan memo girdilerinin ömrü.
 *
 * Normal `MEMO_OMRU_MS` (120sn) BÜYÜTÜLMEDİ — o sabit `isDataCached()`/`run()`
 * arası TUTARLILIK garantisi taşıyor (yukarıdaki yorum) ve bu görev onu
 * bozmamalı. Bulk girdiler ise borcluAlanlariSorgula döngüsünün EN BAŞINDA
 * tek seferde yazılır ama borçlunun ~21 alanı SIRAYLA işlenir; oturum
 * kurtarma turu veya EGM'nin 10 dk'lık kısıtı gibi yavaşlatıcılar araya
 * girerse 120sn çoktan dolmuş olabilir. 15 dk, tek bir borçlunun tüm alan
 * turunu rahatça kapsar; girdi bazında (`Memo.omurMs`) taşınır, genel
 * `MEMO_OMRU_MS`'i etkilemez.
 */
const BULK_MEMO_OMRU_MS = 15 * 60 * 1000;

/**
 * Okuma isteğinin tavan süresi.
 *
 * Snapshot katmanı bir OPTİMİZASYON: zinciri hızlandırmalı, asla geciktirmemeli.
 * `$http` örneklerinde timeout tanımlı değil (icrapro-app `plugins/axios.ts`,
 * vue-imza-app aynısı — satır yorumda bırakılmış); backend takılırsa istek
 * süresiz asılı kalır ve `isDataCached()` üzerinden gece koşumunu bloklardı.
 * Süre dolarsa `null` dönülür ve zincir bugünkü davranışa (IndexedDB -> canlı)
 * düşer. `onYukle()` da aynı tavanı paylaşır (aynı gerekçe).
 */
const ISTEK_TAVANI_MS = 5000;

/**
 * Sunucudaki `SorguSnapshotController::AZAMI_LIMIT` ile BİREBİR aynı (F3/B03):
 * `limit` verilmezse sunucu varsayılanı artık 100 GRUP — `onYukle()` bir
 * borçlunun TÜM alanlarını tek çağrıda istediği için (alan sayısı büyüdükçe/
 * birden çok aday `borclu_ref` grubu oluştuğunda) 100 sınırına takılıp sessizce
 * kesilme riski taşır. `iste()` (tek alan, tek borçlu) bu riski taşımaz — yalnız
 * `onYukle()` açıkça tavana kadar ister.
 */
const AZAMI_LIMIT = 500;

/**
 * B32-2 (Uygulama 2): `dosyayiOnYukle()`'nin `POST /api/v1/sorgu-snapshot/toplu-oku`
 * isteği için AYRI tavan. `onYukle()`nin `ISTEK_TAVANI_MS` (5sn) sabitini
 * PAYLAŞMADI — bu istek TEK GÖVDEDE birden çok borçlunun TÜM alanlarını taşır
 * (dosya başına 1 istek hedefi, bkz. icrapro-app/docs/sorgu-snapshot-sistemi.md §2.2), tek borçluluk
 * `onYukle()`den doğal olarak daha ağırdır. 8 sn: HAR ölçümündeki (23 ardışık
 * GET, ~15sn) hedefin ALTINDA kalacak şekilde seçildi — zincir yine de bir
 * OPTİMİZASYON, süre dolarsa borçlu başına `onYukle()`ye (ya da onun da
 * düşeceği alan-bazlı `iste()`ye) sessizce düşülür.
 */
const DOSYA_ONYUKLEME_ISTEK_TAVANI_MS = 8000;

/**
 * Sunucu sözleşmesi tek istekte en çok 50 borçlu kabul eder (`borclular` 1..50,
 * aşımı 422). Daha kalabalık dosyada liste 50'lik dilimlere bölünür ve dilimler
 * PARALEL gönderilir (bizim API'ye; UYAP'a değil) — aksi halde 51. borçluda
 * istek sessizce reddedilir ve dosyanın hiçbir borçlusu ön-yüklenmezdi.
 */
const DOSYA_ONYUKLEME_DILIM_BOYU = 50;

const memolar = new Map<string, Memo>();

/**
 * NEGATİF ÖNBELLEK (8 Eyl HAR bulgusu): `dosyayiOnYukle()` bir dosya-borçlu için
 * sunucunun TÜM alanlarını tek cevapta getirir; cevapta olmayan alan için
 * sunucuda kayıt YOKTUR. Eskiden bu bilgi tutulmadığı için her eksik alan yine
 * tek bir GET atıp yine boş alıyordu (dosya başına 6-8 boşuna istek). Burada
 * "bu dosya-borçlu için toplu okuma ne zaman yapıldı" tutulur; `get()` memo'da
 * girdi bulamazsa ve toplu okuma tazeyse (BULK_MEMO_OMRU_MS) HTTP atmadan null
 * döner. Cevabı `kesildi` olan borçlu için işaret KONMAZ (liste eksik olabilir).
 */
const tamYuklemeler = new Map<string, number>();

function tamYuklemeAnahtari(dosyaRef: string, kisiKurumId: number): string {
    return dosyaRef + "|" + kisiKurumId;
}

function memoAnahtari(dosyaRef: string, alan: string, kisiKurumId: number | null): string {
    return dosyaRef + "|" + alan + "|" + (kisiKurumId === null ? "" : kisiKurumId);
}

export class SnapshotOkuyucu {
    /**
     * Verilen üçlünün sunucudaki EN SON kaydı; yoksa/hata varsa null.
     *
     * @param kisiKurumId Dosya seviyesi alanlarda null geçilir (sunucu `borclu_ref`
     *                    süzgeci uygulamaz, dosya satırı zaten tekildir).
     * @param vergiNo     Kurum borçlusunun maskesiz VKN'si biliniyorsa; sunucu aday
     *                    kümesine `vkn:` referansını da katar.
     */
    static async get(
        dosyaRef: string,
        alan: string,
        kisiKurumId: number | null = null,
        vergiNo: string | null = null
    ): Promise<SnapshotKaydi | null> {
        if (!dosyaRef || !alan) {
            return null;
        }

        const anahtar = memoAnahtari(dosyaRef, alan, kisiKurumId);
        const memo = memolar.get(anahtar);
        if (memo && Date.now() - memo.zaman < (memo.omurMs ?? MEMO_OMRU_MS)) {
            return SnapshotOkuyucu.klonla(await memo.deger);
        }

        // Negatif önbellek: bu dosya-borçlu için toplu okuma tazeyse ve bu alan
        // memo'ya girmemişse sunucuda kayıt yok — GET atmak boşuna.
        if (kisiKurumId !== null && kisiKurumId > 0) {
            const tamYukleme = tamYuklemeler.get(tamYuklemeAnahtari(dosyaRef, kisiKurumId));
            if (tamYukleme !== undefined && Date.now() - tamYukleme < BULK_MEMO_OMRU_MS) {
                return null;
            }
        }

        const istek = SnapshotOkuyucu.iste(dosyaRef, alan, kisiKurumId, vergiNo);
        memolar.set(anahtar, {zaman: Date.now(), deger: istek});
        SnapshotOkuyucu.memoyuBuda();

        return SnapshotOkuyucu.klonla(await istek);
    }

    /**
     * B32 TOPLU OKUMA: bir borçlunun (veya `borclu_ref=dosya` ile dosya
     * seviyesinin) TÜM alanlarını `alan` VERMEDEN TEK GET ile çekip mevcut
     * memo yapısını alan bazında doldurur. `get()` HİÇBİR DEĞİŞİKLİK
     * GEREKTİRMEDEN bu girdileri bulur — sonraki alan-bazlı çağrılar `iste()`
     * hiç çalıştırmadan memodan döner.
     *
     * Yalnız BORÇLU BAZLI (`kisiKurumId` zorunlu — `> 0`) çağrılır: dosya
     * geneli (`kisi_kurum_id`siz) tek çağrı sunucuda `borclu_ref` süzgeci
     * uygulamaz, dönen satırlar TÜM borçluların karışımı olur ve istemci
     * bunları `kk:`/`tckn:`/`vkn:` arasında AYIRT EDEMEZ — KİŞİ borçlunun
     * TCKN'si UYAP'tan maskeli geldiği için istemci kendi `borclu_ref`ini
     * yeniden üretemez (araştırma notu §5, `BorcluRefCozumleyici.php`
     * öncelik sırası). Bu yüzden dosya-geneli bulk BİLEREK YAPILMAZ; sunucu
     * `kisi_kurum_id`nin kendi `adayReferanslar()` çözümlemesine güvenilir.
     *
     * Alan bulk cevapta YOKSA (o alan için hiç satır yok) memo'ya bilerek
     * YAZILMAZ — `get()` normal akışına düşer, tek bir (boşa çıkan ama
     * zararsız) alan-bazlı GET atar.
     *
     * HATA SESSİZ: `snapshotCevabiKur()`'un try/catch-null deseniyle aynı —
     * bu bir OPTİMİZASYONDUR, başarısızlığı alan-bazlı akışı KIRMAZ.
     */
    static async onYukle(dosyaRef: string, kisiKurumId: number | null, vergiNo: string | null): Promise<void> {
        if (!dosyaRef || kisiKurumId === null || kisiKurumId <= 0) {
            return;
        }

        try {
            const params: Record<string, any> = {
                dosya_ref: dosyaRef,
                kisi_kurum_id: kisiKurumId,
                // F3/B03 (düzeltme): sunucu varsayılanı artık 100 grup —
                // açıkça AZAMI_LIMIT'e kadar istenir, aksi halde alan sayısı
                // büyüdükçe (veya birden çok aday borclu_ref grubu oluşursa)
                // yanıt sessizce kesilebilirdi.
                limit: AZAMI_LIMIT
            };
            if (vergiNo) {
                params.vergi_no = vergiNo;
            }
            // `alan` BİLEREK gönderilmiyor: sunucu `alan` yoksa o dosya/borçlu
            // altındaki TÜM alanların en son satırını tek cevapta döner
            // (SorguSnapshotController@index). `govde`/`include_govde`
            // opt-out'u da BİLEREK gönderilmiyor: sunucu varsayılanı gövdeyi
            // dahil eder — `onYukle`nin tüm amacı gövdeyi önceden memo'ya
            // taşımaktır, opt-out bu amacı boşa çıkarırdı.
            const cevap: any = await Promise.race([
                app.$http.get("/api/v1/sorgu-snapshot", {params}),
                new Promise((_, reddet) =>
                    setTimeout(() => reddet(new Error("Snapshot toplu okuma zaman aşımı")), ISTEK_TAVANI_MS)
                ),
            ]);

            // kod inceleme: `$http`'nin gerçek axios interceptor'ı (icrapro-app/
            // vue-imza-app `plugins/axios.ts`) `response.data`yı döndürüp
            // başlıkları BUGÜN atıyor — yani bu blok bugünkü gerçek koşumda
            // (`cevap.headers` yok) HİÇBİR ZAMAN çalışmaz. Yine de BİLEREK
            // SAVUNMA amaçlı tutuluyor: interceptor ileride tam `response`
            // dönmeye başlarsa `X-Sorgu-Snapshot-Kesildi` yakalanabilsin diye.
            // Davranış `snapshotOkuyucuOnYukle.spec.ts`teki "X-Sorgu-Snapshot-
            // Kesildi" describe bloğuyla kilitli — kaldırılırsa o testler de
            // kaldırılmalı.
            const kesildi = !Array.isArray(cevap)
                && (cevap?.headers?.["x-sorgu-snapshot-kesildi"] === "1"
                    || cevap?.headers?.["X-Sorgu-Snapshot-Kesildi"] === "1");
            if (kesildi) {
                console.warn(
                    "SnapshotOkuyucu.onYukle: sunucu yanıtı AZAMI_LIMIT=" + AZAMI_LIMIT
                    + " grup tavanında kesildi — bazı alanlar toplu ön-yüklemeye giremedi, alan-bazlı akış devreye girecek."
                );
            }

            const govde = Array.isArray(cevap) ? cevap : cevap?.data;
            if (!Array.isArray(govde) || govde.length === 0) {
                return;
            }

            // kod inceleme: gruplama + en-yeniyi-seçme + guard'lı memo yazma
            // `dilimiOnYukle()`nin aynı işi yaptığı bloğuyla ortaktı, tek
            // yardımcıda birleştirildi (bkz. `kayitlariAlanaGoreMemoyaYaz`).
            SnapshotOkuyucu.kayitlariAlanaGoreMemoyaYaz(dosyaRef, kisiKurumId, govde, Date.now());

            SnapshotOkuyucu.memoyuBuda();
        } catch (e) {
            // Sessiz: toplu ön-yükleme başarısız olursa alan-bazlı akış
            // (kendi `iste()`'si) hiçbir değişiklik gerektirmeden devreye
            // girer — plan §Sınırlar.
            console.warn("SnapshotOkuyucu: toplu ön-yükleme başarısız, alan-bazlı akışa düşülüyor.", e);
        }
    }

    /**
     * B32-2 (Uygulama 2, DEV-2603): DOSYA GENELİ toplu okuma — HAR ölçümünde
     * haciz hazırlama 2 dosya için 23 ardışık GET attığı bulgusuna karşılık,
     * bir dosyanın TÜM borçlularının TÜM alanlarını `POST
     * /api/v1/sorgu-snapshot/toplu-oku` ile TEK istekte çekip mevcut alan
     * bazlı memo yapısını (`memoAnahtari(dosyaRef, alan, kisiKurumId)`) doldurur.
     * `get()`/`onYukle()` HİÇBİR DEĞİŞİKLİK GEREKTİRMEDEN bu girdileri bulur.
     *
     * `onYukle()`den FARKI: o TEK borçlunun tüm alanlarını GET ile ister (borçlu
     * başına 1 istek); bu metod ise TÜM borçluları TEK POST gövdesinde toplar
     * (dosya başına 1 istek) — sunucu ucu (`SorguSnapshotController@topluOku`)
     * her borçluyu kendi `BorcluRefCozumleyici::adayReferanslar()`iyle AYRI AYRI
     * çözüp TEK transaction içinde okur (sözleşme, görev dokümanı).
     *
     * GERİYE UYUM: sunucu bu ucu henüz bilmiyorsa (eski backend, 404/405) borçlu
     * başına MEVCUT `onYukle()`ye düşülür — aynı memo yapısını dolduran, halihazırda
     * kanıtlanmış yol. Başka bir hata (ağ, 5xx, zaman aşımı) SESSİZCE yutulur:
     * bu bir OPTİMİZASYONDUR, hiçbir dalı sorgu zincirini KIRMAMALI (plan §Sınırlar).
     */
    static async dosyayiOnYukle(dosyaRef: string, borclular: DosyaOnYuklemeBorclusu[]): Promise<void> {
        if (!dosyaRef || !Array.isArray(borclular)) {
            return;
        }

        // kisiKurumId <= 0 (ya da sayı değil) olan borçlular sunucu tarafında
        // zaten aday üretemez (adayReferanslar `kisi_kurum_id >= 1` ister) —
        // baştan elenir; hiç geçerli borçlu kalmazsa istek HİÇ ATILMAZ.
        const gecerliBorclular = borclular.filter(
            (b) => b && typeof b.kisiKurumId === "number" && b.kisiKurumId > 0
        );
        if (gecerliBorclular.length === 0) {
            return;
        }

        const dilimler: DosyaOnYuklemeBorclusu[][] = [];
        for (let i = 0; i < gecerliBorclular.length; i += DOSYA_ONYUKLEME_DILIM_BOYU) {
            dilimler.push(gecerliBorclular.slice(i, i + DOSYA_ONYUKLEME_DILIM_BOYU));
        }
        await Promise.all(dilimler.map((dilim) => SnapshotOkuyucu.dilimiOnYukle(dosyaRef, dilim)));
    }

    /** Tek dilim (≤50 borçlu) için toplu-oku POST'u ve memo doldurma; hata dalları dosyayiOnYukle sözleşmesi. */
    private static async dilimiOnYukle(dosyaRef: string, gecerliBorclular: DosyaOnYuklemeBorclusu[]): Promise<void> {
        try {
            const govde = {
                dosya_ref: dosyaRef,
                borclular: gecerliBorclular.map((b) => ({
                    kisi_kurum_id: b.kisiKurumId,
                    vergi_no: b.vergiNo || null
                }))
                // `govde` (gövde dahil etme) opt-out'u BİLEREK gönderilmiyor —
                // sözleşmedeki sunucu varsayılanı zaten `true` (onYukle()'deki
                // aynı gerekçe: bu metodun tüm amacı gövdeyi memo'ya taşımak).
            };

            const cevap: any = await Promise.race([
                app.$http.post("/api/v1/sorgu-snapshot/toplu-oku", govde),
                new Promise((_, reddet) =>
                    setTimeout(
                        () => reddet(new Error("Snapshot dosya toplu okuma zaman aşımı")),
                        DOSYA_ONYUKLEME_ISTEK_TAVANI_MS
                    )
                ),
            ]);

            // Üretimde `$http` interceptor'ı `response.data`yı doğrudan döner —
            // sözleşme gövdesi bu yüzden PRATİKTE `cevap` üzerinde bulunur;
            // yine de (testlerde/ileride) tam `{data: {...}}` şekline karşı da
            // savunmalı okunur (iste()/onYukle() ile aynı desen).
            const govdeCevap: any = Array.isArray(cevap?.borclular) ? cevap : cevap?.data;
            const borcluSonuclari = govdeCevap?.borclular;
            if (!Array.isArray(borcluSonuclari) || borcluSonuclari.length === 0) {
                return;
            }

            const simdi = Date.now();
            for (const sonuc of borcluSonuclari) {
                const kisiKurumId = Number(sonuc?.kisi_kurum_id);
                if (!kisiKurumId || kisiKurumId <= 0) {
                    continue;
                }

                if (sonuc?.kesildi === true) {
                    console.warn(
                        "SnapshotOkuyucu.dosyayiOnYukle: borçlu " + kisiKurumId
                        + " için sunucu yanıtı AZAMI_LIMIT tavanında kesildi — bazı alanlar"
                        + " toplu ön-yüklemeye giremedi, alan-bazlı akış devreye girecek."
                    );
                }

                // Negatif önbellek işareti: cevap tam (kesilmemiş) ise bu borçlunun
                // sunucudaki TÜM alanları elimizde — cevapta olmayan alan yok demek.
                // Boş kayıt listesi de bilgidir (hiç snapshot'ı yok).
                if (sonuc?.kesildi !== true) {
                    tamYuklemeler.set(tamYuklemeAnahtari(dosyaRef, kisiKurumId), simdi);
                }

                const kayitlar = sonuc?.kayitlar;
                if (!Array.isArray(kayitlar) || kayitlar.length === 0) {
                    continue;
                }

                // onYukle()'deki AYNI mantık (gruplama + guard'lı en-yeniyi-yaz):
                // ortak yardımcıya taşındı, bkz. `kayitlariAlanaGoreMemoyaYaz`.
                SnapshotOkuyucu.kayitlariAlanaGoreMemoyaYaz(dosyaRef, kisiKurumId, kayitlar, simdi);
            }

            SnapshotOkuyucu.memoyuBuda();
        } catch (e) {
            // Sunucu bu ucu bilmiyorsa (henüz deploy edilmemiş eski backend):
            // borçlu başına MEVCUT onYukle()'ye düş — aynı memo yapısını dolduran,
            // halihazırda kanıtlanmış tek-borçlu toplu GET yolu.
            const status = (e as any)?.response?.status;
            if (status === 404 || status === 405) {
                await Promise.all(
                    gecerliBorclular.map((b) => SnapshotOkuyucu.onYukle(dosyaRef, b.kisiKurumId, b.vergiNo))
                );
                return;
            }
            // Diğer hatalar (ağ, 5xx, zaman aşımı) BİLEREK SESSİZ: bu bir
            // OPTİMİZASYONDUR, başarısızlığı alan-bazlı akışı KIRMAZ — burada
            // console.warn dahi ATILMAZ ("borçlu başına GET fallback" YALNIZ
            // sunucunun ucu BİLMEDİĞİ 404/405 durumunda anlamlıdır).
        }
    }

    private static async iste(
        dosyaRef: string,
        alan: string,
        kisiKurumId: number | null,
        vergiNo: string | null
    ): Promise<SnapshotKaydi | null> {
        try {
            const params: Record<string, any> = {dosya_ref: dosyaRef, alan};
            if (kisiKurumId !== null && kisiKurumId > 0) {
                params.kisi_kurum_id = kisiKurumId;
            }
            if (vergiNo) {
                params.vergi_no = vergiNo;
            }

            const cevap: any = await Promise.race([
                app.$http.get("/api/v1/sorgu-snapshot", {params}),
                new Promise((_, reddet) =>
                    setTimeout(() => reddet(new Error("Snapshot okuma zaman aşımı")), ISTEK_TAVANI_MS)
                ),
            ]);

            // icrapro-app'te axios interceptor'ı `response.data`yı döndürüyor,
            // vue-imza-app'te de öyle; yine de iki şekle karşı da savunmalı okunur.
            const govde = Array.isArray(cevap) ? cevap : cevap?.data;
            if (!Array.isArray(govde) || govde.length === 0) {
                return null;
            }

            // Sunucu (alan, borclu_ref) başına en son satırı döndürüyor. `alan` ve
            // `kisi_kurum_id` verildiğinde liste pratikte tek elemanlı; birden çok
            // aday referans eşleşirse ilkini almak yerine EN YENİ olan seçilir.
            const kayitlar = govde.filter((k: any) => k && Array.isArray(k.entities));
            if (kayitlar.length === 0) {
                return null;
            }

            // kod inceleme: bu reduce BİLEREK `enYeniyiSec()` ortak yardımcısını
            // ÇAĞIRMAZ — accumulator'ı (`enYeni`) İLK eleman alıp yalnız kesin
            // DAHA YENİ bir aday geldiğinde değiştirir; eşit damgalı iki aday
            // varsa dizideki İLK'i korur. `enYeniyiSec()` ise (a,b)=>damga(a)>
            // damga(b)?a:b kalıbıyla eşitlikte dizideki SON adayı korur —
            // `onYukle()`/`dilimiOnYukle()`nin kendi tie-break'i budur ve bu iki
            // kural birbirinin TERSİ. Ortak yardımcıya taşınıp buradan da
            // çağrıldığında bu satırın tie-break yönü sessizce tersine
            // dönüyordu (kod inceleme bulgusu); üç çağıranın da KENDİ eski
            // davranışını koruması için burası ayrı tutuldu.
            return kayitlar.reduce((enYeni: any, aday: any) =>
                SnapshotOkuyucu.damga(aday) > SnapshotOkuyucu.damga(enYeni) ? aday : enYeni
            ) as SnapshotKaydi;
        } catch (e) {
            // Sessiz: sunucuya ulaşılamaması sorgu zincirini KIRMAZ (plan §Sınırlar).
            console.warn("SnapshotOkuyucu: snapshot okunamadı, canlı zincire düşülüyor.", e);

            return null;
        }
    }

    /**
     * Ömrü dolmuş memo kayıtlarını atar.
     *
     * NEDEN: bot uygulaması günlerce açık kalıyor ve gece koşumu binlerce
     * (dosya_ref, alan, borçlu) üçlüsü görüyor. Tablo hiç budanmasa her üçlü
     * için bir Promise sonsuza dek bellekte kalırdı. Budama yazma anında ve
     * yalnız SÜRESİ DOLMUŞ kayıtlar üzerinde yapılır — canlı memo bozulmaz.
     */
    private static memoyuBuda(): void {
        const simdi = Date.now();
        memolar.forEach((memo, anahtar) => {
            if (simdi - memo.zaman >= (memo.omurMs ?? MEMO_OMRU_MS)) {
                memolar.delete(anahtar);
            }
        });
        tamYuklemeler.forEach((zaman, anahtar) => {
            if (simdi - zaman >= BULK_MEMO_OMRU_MS) {
                tamYuklemeler.delete(anahtar);
            }
        });
    }

    /**
     * B37: memo AYNI referansı tekrar tekrar döndürüyordu — aynı pencerede
     * (120sn/15dk) gelen HER çağıran aynı nesneyi alıyordu. `UyapRequest.
     * snapshotCevabiKur()` bu gövdeyi yerinde mutasyona uğratıyor
     * (`snapshotKaynagi`/`snapshotDamgasi` ekliyor) ve alan sınıflarının
     * `parse()`'ları da entity/aracList dizilerini yerinde zenginleştiriyor
     * (`selected`, `message`, vb.) — aynı pencerede aynı üçlüyü sorgulayan
     * iki bağımsız tüketici birbirinin sonradan yazdığı alanları görebilirdi.
     * JSON round-trip klon her çağırana KENDİ nesnesini verir; memo'daki asıl
     * kayıt DEĞİŞMEZ. Round-trip yeterli: kayıt zaten bir HTTP JSON
     * cevabından geliyor (fonksiyon/Date/Map gibi JSON-dışı alan taşımaz).
     */
    private static klonla<T>(deger: T): T {
        if (deger === null || deger === undefined) {
            return deger;
        }

        return JSON.parse(JSON.stringify(deger));
    }

    /**
     * kod inceleme: `onYukle()` ve `dilimiOnYukle()` (ikisi de
     * `kayitlariAlanaGoreMemoyaYaz()` üzerinden) aynı "aday kayıtlar arasından
     * en güncel damgalıyı seç" reduce'unu ayrı ayrı yazıyordu; ortak yardımcıya
     * taşındı. `kayitlar` en az bir eleman içermelidir (çağıranlar zaten boş
     * grup üretmiyor).
     *
     * DİKKAT — tie-break yönü: eşit damgalı iki adayda (a,b) sırasıyla
     * karşılaştırılırken damga(a) > damga(b) DEĞİLSE `b` (dizideki SONRAKİ
     * aday) döner. `iste()` KASITLI OLARAK bu yardımcıyı çağırmaz — onun eski
     * (ve davranışça doğrulanmış) tie-break'i tam TERSİ yöndedir (eşitlikte
     * İLK adayı korur, bkz. `iste()` içindeki yorum). Bu yardımcıyı `iste()`
     * için de kullanmaya kalkışmak o yolun tie-break yönünü sessizce
     * tersine çevirir.
     */
    private static enYeniyiSec(kayitlar: any[]): any {
        return kayitlar.reduce((a, b) =>
            SnapshotOkuyucu.damga(a) > SnapshotOkuyucu.damga(b) ? a : b
        );
    }

    /**
     * kod inceleme: `onYukle()` ile `dilimiOnYukle()`nin borçlu döngüsü,
     * kayıtları `kayit.alan`a göre gruplayıp her grup için (a) hâlâ GEÇERLİ
     * bir memo girdisini EZMEDEN, (b) `enYeniyiSec()` ile en güncel kaydı
     * seçip `BULK_MEMO_OMRU_MS` ömürlü memoya yazan birebir aynı bloğu iki
     * yerde tekrarlıyordu; tek yardımcıda birleştirildi. Davranış değişmedi.
     *
     * (düzeltme) guard'ı: mevcut memo girdisi hâlâ TAZEYSE (ör. ekrandan az
     * önce `SnapshotOkuyucu.get()` ile 120sn ömürlü yazılmış) burada sessizce
     * 15dk'lık `BULK_MEMO_OMRU_MS`'e uzatılıp EZİLMEZ (bkz. dosya-başı
     * `MEMO_OMRU_MS` yorumu — `isDataCached()`/`run()` çifti arasındaki
     * tutarlılık garantisi bundan etkilenmemeli).
     */
    private static kayitlariAlanaGoreMemoyaYaz(
        dosyaRef: string,
        kisiKurumId: number,
        kayitlar: any[],
        simdi: number
    ): void {
        const alanBazinda = new Map<string, any[]>();
        for (const kayit of kayitlar) {
            if (!kayit || !kayit.alan || !Array.isArray(kayit.entities)) {
                continue;
            }
            const liste = alanBazinda.get(kayit.alan) ?? [];
            liste.push(kayit);
            alanBazinda.set(kayit.alan, liste);
        }

        alanBazinda.forEach((adaylar, alan) => {
            const anahtar = memoAnahtari(dosyaRef, alan, kisiKurumId);

            const mevcut = memolar.get(anahtar);
            if (mevcut && simdi - mevcut.zaman < (mevcut.omurMs ?? MEMO_OMRU_MS)) {
                return;
            }

            const enYeni = SnapshotOkuyucu.enYeniyiSec(adaylar);
            memolar.set(anahtar, {
                zaman: simdi,
                deger: Promise.resolve(enYeni as SnapshotKaydi),
                omurMs: BULK_MEMO_OMRU_MS
            });
        });
    }

    private static damga(kayit: any): number {
        const degerler = [kayit?.fetched_at, kayit?.last_seen_at]
            .map((d) => (d ? new Date(d).getTime() : NaN))
            .filter((t) => !isNaN(t));

        return degerler.length === 0 ? 0 : Math.max(...degerler);
    }

    /** Yalnız testler için: memo tablosunu boşaltır. */
    static memoyuTemizle(): void {
        memolar.clear();
        tamYuklemeler.clear();
    }
}
